/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useCallback, useEffect, useState } from 'react';
import { OpenAPIHousien } from '@/services/requests/core/OpenAPI';
import { request as __request } from '@/services/requests/core/request';

export interface PropertyMetrics {
  total_visits: number;
  total_leads: number;
  days_on_market: number;
  total_views: number;
  total_shares: number;
}

interface UsePropertyMetricsState {
  metrics: PropertyMetrics | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loads aggregate performance metrics (visits, leads, views, shares) for a property.
 * @param agencyId - The agency that owns the property.
 * @param propertyId - The property to fetch metrics for.
 * @returns The metrics, loading flag, error, and a refetch callback.
 */
export function usePropertyMetrics(agencyId: string, propertyId: string) {
  const [state, setState] = useState<UsePropertyMetricsState>({
    metrics: null,
    loading: true,
    error: null,
  });

  const fetchMetrics = useCallback(async () => {
    if (!agencyId || !propertyId) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await __request<PropertyMetrics>(OpenAPIHousien, {
        method: 'GET',
        url: '/agencies/{agencyId}/properties/{propertyId}/metrics',
        path: { agencyId, propertyId },
      });
      setState({ metrics: data, loading: false, error: null });
    } catch (err) {
      setState({
        metrics: null,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load metrics',
      });
    }
  }, [agencyId, propertyId]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return { ...state, refetch: fetchMetrics };
}
